import { t } from "@redotech/redo-i18n";
import type { MerchantRoutes } from "@redotech/redo-merchant-app-common/merchant-routes";
import {
  canReadSetting,
  getSettingCatalogDefinitionsForGroup,
  getSettingCatalogGroups,
  type SettingDefinition,
  type SettingGroupDefinition,
  type SettingVisibility,
} from "@redotech/redo-settings-common/settings-catalog";
import {
  searchSettingsCatalog,
  type SettingsSearchGroupResult,
} from "@redotech/redo-settings-common/settings-catalog/search";
import {
  getSettingSearchEvidence,
  settingsSectionToFriendly,
  type SettingItem,
  type SettingItemSearchMatch,
  type SettingSearchEvidence,
  type SettingsSection,
} from "@redotech/redo-web/arbiter-components/navigation/redo-settings-nav";
import { settingsSectionToFriendly as newSettingsSectionToFriendly } from "@redotech/redo-web/arbiter-components/navigation/redo-settings-nav.new";
import { type CommandPaletteItem } from "@redotech/redo-web/command-palette/contexts/command-palette-context";
import { getSettingsSearchRank } from "@redotech/redo-web/settings-search/settings-search-rank";
import Fuse from "fuse.js";
import {
  getSettingsFocusedGroupHref,
  getSettingsFocusedSettingHref,
} from "./settings-agent/settings-deep-links";
import { getSettingsGroupFullPageHref } from "./settings-agent/settings-group-full-page-links";

export type SettingsNavigationSearchRoutes = Pick<
  MerchantRoutes,
  | "contextualHref"
  | "getSettingsSubRoute"
  | "getOrdersOutboundLabelsSubRoute"
  | "legacySupportBaseUrl"
  | "settingsHref"
>;

interface AccessibleSettingsNavigationItem {
  canonicalHref: string;
  item: SettingItem;
  sectionName: string;
}

export interface SettingsNavigationCommandPaletteItem extends CommandPaletteItem {
  dedupeHrefs: readonly string[];
  href: string;
}

interface SettingsNavigationSearchCandidate {
  catalogScore: number;
  groupDescription: string;
  groupSearchTerms: readonly string[];
  groupTitle: string;
  item: SettingsNavigationCommandPaletteItem;
  matchedSettingDescriptions: readonly string[];
  matchedSettingLabels: readonly string[];
  matchedSettingSearchTerms: readonly string[];
  matchedSettingRecords: readonly { label: string; terms: readonly string[] }[];
  pageFriendlyName: string;
  pageTitle: string;
  routeSearchTerms: readonly string[];
  searchEvidence?: SettingSearchEvidence;
}

const DEFAULT_VIEWER_VISIBILITY: SettingVisibility = "merchant";
const DEFAULT_SETTINGS_NAVIGATION_SEARCH_LIMIT = 5;
const MAX_MATCHED_SETTING_LABELS = 3;
const SETTINGS_NAVIGATION_SEARCH_STOP_WORDS = new Set([
  "page",
  "setting",
  "settings",
]);
const VALUE_SEMANTICS_OPTION_SEARCH_STOP_WORDS = new Set([
  "custom",
  "gmail",
  "outlook",
]);
const SETTINGS_NAVIGATION_SEARCH_KEYS = [
  { name: "pageFriendlyName", weight: 8 },
  { name: "pageTitle", weight: 6 },
  { name: "matchedSettingLabels", weight: 5 },
  { name: "groupTitle", weight: 4 },
  { name: "matchedSettingSearchTerms", weight: 3 },
  { name: "groupSearchTerms", weight: 2 },
  { name: "groupDescription", weight: 1 },
  { name: "matchedSettingDescriptions", weight: 1 },
  { name: "routeSearchTerms", weight: 1 },
] as const;

export function addSettingsCatalogSearchTerms({
  shippingNewSettings = false,
  routes,
  sections,
  viewerVisibility = DEFAULT_VIEWER_VISIBILITY,
}: {
  shippingNewSettings?: boolean;
  routes: SettingsNavigationSearchRoutes;
  sections: readonly SettingsSection[];
  viewerVisibility?: SettingVisibility;
}): SettingsSection[] {
  const accessibleItems = getAccessibleSettingsNavigationItems(
    sections,
    shippingNewSettings,
  );
  const itemSearchHints = new Map<SettingItem, Set<string>>();
  const itemSearchMatches = new Map<SettingItem, SettingItemSearchMatch[]>();
  const itemSearchGroups = new Map<SettingItem, SettingItemSearchMatch[]>();
  const itemSearchTerms = new Map<SettingItem, Set<string>>();

  for (const group of getSettingCatalogGroups(shippingNewSettings)) {
    const definitions = getVisibleDefinitionsForGroup({
      groupId: group.id,
      viewerVisibility,
      shippingNewSettings,
    });
    if (
      (!shippingNewSettings && definitions.length === 0) ||
      !canReadSetting(viewerVisibility, group.access)
    ) {
      continue;
    }

    const href = getSettingsGroupFullPageHref({
      groupId: group.id,
      routes,
      shippingNewSettings,
    });
    const accessibleItem = findAccessibleSettingsNavigationItem({
      accessibleItems,
      href,
      routes,
    });
    if (!accessibleItem) {
      continue;
    }

    const terms = getCatalogSearchTerms({
      definitions,
      group,
      href,
      shippingNewSettings,
    });
    const hints = getCatalogSearchHints({ definitions, group });
    const matches = getCatalogSearchMatches({
      definitions,
      group,
      href,
      shippingNewSettings,
    });
    if (shippingNewSettings) {
      addSearchMatch(
        getOrCreateSearchMatches(itemSearchGroups, accessibleItem.item),
        {
          href: getSettingsFocusedGroupHref({ groupId: group.id, href }),
          label: group.title,
          searchTerms: getUniqueStrings([
            ...getGroupVisibleSearchTerms(group),
            ...definitions
              .filter((definition) => definition.metadata.label === group.title)
              .flatMap((definition) =>
                getCatalogDefinitionVisibleSearchTerms(definition, true),
              ),
          ]),
        },
      );
    }
    const targetHints = getOrCreateSearchValues(
      itemSearchHints,
      accessibleItem.item,
    );
    for (const hint of hints) {
      addSearchValue(targetHints, hint);
    }

    const targetMatches = getOrCreateSearchMatches(
      itemSearchMatches,
      accessibleItem.item,
    );
    for (const match of matches) {
      addSearchMatch(targetMatches, match);
    }

    const targetTerms = getOrCreateSearchTerms(
      itemSearchTerms,
      accessibleItem.item,
    );
    for (const term of terms) {
      addSearchValue(targetTerms, term);
    }
  }

  return sections.map((section) => ({
    ...section,
    items: addSettingsCatalogSearchTermsToItems({
      items: section.items,
      shippingNewSettings,
      itemSearchHints,
      itemSearchMatches,
      itemSearchGroups,
      itemSearchTerms,
    }),
  }));
}

function addSettingsCatalogSearchTermsToItems({
  items,
  shippingNewSettings,
  itemSearchHints,
  itemSearchMatches,
  itemSearchGroups,
  itemSearchTerms,
}: {
  items: readonly SettingItem[];
  shippingNewSettings: boolean;
  itemSearchHints: ReadonlyMap<SettingItem, Set<string>>;
  itemSearchMatches: ReadonlyMap<SettingItem, SettingItemSearchMatch[]>;
  itemSearchGroups: ReadonlyMap<SettingItem, SettingItemSearchMatch[]>;
  itemSearchTerms: ReadonlyMap<SettingItem, Set<string>>;
}): SettingItem[] {
  return items.map((item) => {
    const children =
      shippingNewSettings && item.children
        ? addSettingsCatalogSearchTermsToItems({
            items: item.children,
            shippingNewSettings,
            itemSearchHints,
            itemSearchMatches,
            itemSearchGroups,
            itemSearchTerms,
          })
        : undefined;
    const hints = itemSearchHints.get(item);
    const matches = itemSearchMatches.get(item);
    const groups = itemSearchGroups.get(item);
    const terms = itemSearchTerms.get(item);
    if (
      !hints?.size &&
      !matches?.length &&
      !groups?.length &&
      !terms?.size &&
      !item.searchHints?.length &&
      !item.searchMatches?.length &&
      (!shippingNewSettings || !item.searchGroups?.length) &&
      !item.searchTerms?.length
    ) {
      return children ? { ...item, children } : item;
    }

    const mergedHints = new Set<string>();
    for (const hint of item.searchHints ?? []) {
      addSearchValue(mergedHints, hint);
    }
    for (const hint of hints ?? []) {
      addSearchValue(mergedHints, hint);
    }

    const mergedMatches = [...(item.searchMatches ?? [])];
    for (const match of matches ?? []) {
      addSearchMatch(mergedMatches, match);
    }
    const mergedGroups = [...(item.searchGroups ?? [])];
    for (const group of groups ?? []) {
      addSearchMatch(mergedGroups, group);
    }

    const mergedTerms = new Set<string>();
    for (const term of item.searchTerms ?? []) {
      addSearchValue(mergedTerms, term);
    }
    for (const term of terms ?? []) {
      addSearchValue(mergedTerms, term);
    }

    return {
      ...item,
      ...(shippingNewSettings ? { children, searchGroups: mergedGroups } : {}),
      searchHints: [...mergedHints],
      searchMatches: mergedMatches,
      searchTerms: [...mergedTerms],
    };
  });
}

export function searchSettingsNavigationItems({
  shippingNewSettings = false,
  limit = DEFAULT_SETTINGS_NAVIGATION_SEARCH_LIMIT,
  navigate,
  query,
  routes,
  sections,
  viewerVisibility = DEFAULT_VIEWER_VISIBILITY,
}: {
  shippingNewSettings?: boolean;
  limit?: number;
  navigate: (href: string) => void;
  query: string;
  routes: SettingsNavigationSearchRoutes;
  sections: readonly SettingsSection[];
  viewerVisibility?: SettingVisibility;
}): SettingsNavigationCommandPaletteItem[] {
  const trimmedQuery = query.trim();
  if (!trimmedQuery || sections.length === 0) {
    return [];
  }

  const accessibleItems = getAccessibleSettingsNavigationItems(
    sections,
    shippingNewSettings,
  );
  const seenHrefs = new Set<string>();

  const candidates = searchSettingsCatalog({
    shippingNewSettings,
    query: trimmedQuery,
    limit: Math.max(limit * 4, 20),
    viewerVisibility,
  }).groups.flatMap((groupResult) => {
    const href = getSettingsGroupFullPageHref({
      shippingNewSettings,
      groupId: groupResult.id,
      routes,
    });
    const accessibleItem = findAccessibleSettingsNavigationItem({
      accessibleItems,
      href,
      routes,
    });
    if (!accessibleItem) {
      return [];
    }

    if (seenHrefs.has(href)) {
      return [];
    }
    seenHrefs.add(href);

    return [
      createSettingsNavigationSearchCandidate({
        shippingNewSettings,
        accessibleItem,
        groupResult,
        href,
        navigate,
        routes,
        viewerVisibility,
      }),
    ];
  });

  const rankedCandidates = searchSettingsNavigationCandidates({
    candidates,
    query: trimmedQuery,
    shippingNewSettings,
  });
  if (!shippingNewSettings) {
    return rankedCandidates.slice(0, limit).map((candidate) => candidate.item);
  }
  const pages = new Map<string, SettingsNavigationCommandPaletteItem[]>();
  for (const { item } of rankedCandidates) {
    const pageId = item.settingsResult?.pageId ?? item.id;
    const pageItems = pages.get(pageId) ?? [];
    if (!pages.has(pageId)) {
      pages.set(pageId, pageItems);
    }
    if (pageItems.some((result) => result.settingsResult?.kind === "page")) {
      continue;
    }
    if (item.settingsResult?.kind === "page") {
      pageItems.length = 0;
    }
    pageItems.push(item);
  }
  return [...pages.values()].flat().slice(0, limit);
}

export function removeSettingsNavigationDuplicatePageResults<
  TItem extends CommandPaletteItem,
>({
  pageResults,
  settingsResults,
}: {
  pageResults: readonly TItem[];
  settingsResults: readonly SettingsNavigationCommandPaletteItem[];
}): TItem[] {
  const settingsHrefs = new Set(
    settingsResults.flatMap((result) =>
      result.dedupeHrefs.map(canonicalizeSettingsNavigationHref),
    ),
  );

  return pageResults.filter((pageResult) => {
    return !settingsHrefs.has(
      canonicalizeSettingsNavigationHref(pageResult.id),
    );
  });
}

export function canonicalizeSettingsNavigationHref(href: string): string {
  try {
    const url = new URL(href, "https://redo.local");
    return normalizePathname(url.pathname);
  } catch {
    const withoutHash = href.split("#")[0] ?? "";
    const withoutQuery = withoutHash.split("?")[0] ?? "";
    return normalizePathname(withoutQuery);
  }
}

function settingsSearchGroupToCommandPaletteItem({
  shippingNewSettings,
  accessibleItem,
  groupResult,
  href,
  navigate,
  routes,
}: {
  shippingNewSettings: boolean;
  accessibleItem: AccessibleSettingsNavigationItem;
  groupResult: SettingsSearchGroupResult;
  href: string;
  navigate: (href: string) => void;
  routes: SettingsNavigationSearchRoutes;
}): SettingsNavigationCommandPaletteItem {
  const title = [
    shippingNewSettings ? t("Settings") : "Settings",
    accessibleItem.sectionName,
    accessibleItem.item.friendlyName,
  ].join(" > ");
  const matchedSettingHref = groupResult.matchedSettings[0]?.id
    ? getSettingsFocusedSettingHref({
        groupId: groupResult.id,
        href,
        settingId: groupResult.matchedSettings[0].id,
      })
    : getSettingsFocusedGroupHref({ groupId: groupResult.id, href });

  return {
    id: matchedSettingHref,
    href: matchedSettingHref,
    dedupeHrefs: getUniqueStrings([
      href,
      accessibleItem.item.href,
      ...getCanonicalRouteCandidates(href, routes),
    ]),
    title,
    subtitle: getMatchedSettingsSubtitle(groupResult, shippingNewSettings),
    category: "pages",
    group: shippingNewSettings ? t("Settings") : "Settings",
    ...(shippingNewSettings
      ? {
          settingsResult: {
            pageId: accessibleItem.item.id,
            pageTitle: accessibleItem.item.friendlyName,
            PageIcon: accessibleItem.item.UnfilledIcon,
            label: groupResult.matchedSettings[0]?.label ?? groupResult.title,
            kind: groupResult.matchedSettings.length
              ? ("setting" as const)
              : ("group" as const),
          },
        }
      : {}),
    action: ({ newTab }) => {
      if (newTab) {
        window.open(matchedSettingHref, "_blank");
      } else {
        navigate(matchedSettingHref);
      }
    },
  };
}

function getMatchedSettingsSubtitle(
  groupResult: SettingsSearchGroupResult,
  shippingNewSettings: boolean,
): string {
  const matchedLabels = groupResult.matchedSettings
    .map((setting) => setting.label)
    .slice(0, MAX_MATCHED_SETTING_LABELS);

  if (matchedLabels.length > 0) {
    if (!shippingNewSettings) {
      const prefix =
        matchedLabels.length === 1 ? "Matched setting" : "Matched settings";
      return `${prefix}: ${matchedLabels.join(", ")}`;
    }
    return t("{{count}} matching settings: {{labels}}", {
      count: matchedLabels.length,
      labels: matchedLabels.join(", "),
    });
  }

  return groupResult.description;
}

function createSettingsNavigationSearchCandidate({
  shippingNewSettings,
  accessibleItem,
  groupResult,
  href,
  navigate,
  routes,
  viewerVisibility,
}: {
  shippingNewSettings: boolean;
  accessibleItem: AccessibleSettingsNavigationItem;
  groupResult: SettingsSearchGroupResult;
  href: string;
  navigate: (href: string) => void;
  routes: SettingsNavigationSearchRoutes;
  viewerVisibility: SettingVisibility;
}): SettingsNavigationSearchCandidate {
  const pageTitle = [
    shippingNewSettings ? t("Settings") : "Settings",
    accessibleItem.sectionName,
    accessibleItem.item.friendlyName,
  ].join(" ");
  const group = getSettingCatalogGroups(shippingNewSettings).find(
    (candidateGroup) => candidateGroup.id === groupResult.id,
  );
  const definitions = getVisibleDefinitionsForGroup({
    groupId: groupResult.id,
    viewerVisibility,
    shippingNewSettings,
  });
  const matchedSettingIds = new Set(
    groupResult.matchedSettings.map((setting) => setting.id),
  );
  const matchedDefinitions = definitions.filter((definition) =>
    matchedSettingIds.has(definition.id),
  );
  return {
    catalogScore: groupResult.score,
    groupDescription: groupResult.description,
    groupSearchTerms: getGroupVisibleSearchTerms(group),
    groupTitle: groupResult.title,
    item: settingsSearchGroupToCommandPaletteItem({
      shippingNewSettings,
      accessibleItem,
      groupResult,
      href,
      navigate,
      routes,
    }),
    matchedSettingDescriptions: groupResult.matchedSettings.map(
      (setting) => setting.description,
    ),
    matchedSettingLabels: groupResult.matchedSettings.map(
      (setting) => setting.label,
    ),
    matchedSettingSearchTerms: matchedDefinitions.flatMap((definition) =>
      getCatalogDefinitionVisibleSearchTerms(definition, shippingNewSettings),
    ),
    matchedSettingRecords: matchedDefinitions.map((definition) => ({
      label: definition.metadata.label,
      terms: getCatalogDefinitionVisibleSearchTerms(
        definition,
        shippingNewSettings,
      ),
    })),
    pageFriendlyName: accessibleItem.item.friendlyName,
    pageTitle,
    routeSearchTerms: getSettingsNavigationRouteSearchTerms([
      accessibleItem.item.href,
      href,
      ...getCanonicalRouteCandidates(href, routes),
    ]),
  };
}

function searchSettingsNavigationCandidates({
  candidates,
  shippingNewSettings,
  query,
}: {
  shippingNewSettings: boolean;
  candidates: readonly SettingsNavigationSearchCandidate[];
  query: string;
}): SettingsNavigationSearchCandidate[] {
  const searcher = new Fuse(candidates, {
    keys: [...SETTINGS_NAVIGATION_SEARCH_KEYS],
    includeScore: true,
    ignoreLocation: true,
    minMatchCharLength: 1,
    shouldSort: true,
    threshold: 0.35,
  });
  const candidatesByHref = new Map<
    string,
    { candidate: SettingsNavigationSearchCandidate; fuseScore: number }
  >();

  for (const queryVariant of getSettingsNavigationSearchQueryVariants(query)) {
    for (const result of searcher.search(queryVariant)) {
      const searchEvidence = getSettingSearchEvidence({
        groupLabel: result.item.groupTitle,
        groupValues: getSettingsNavigationCandidateGroupSearchValues(
          result.item,
        ),
        pageLabel: result.item.pageFriendlyName,
        pageValues: getSettingsNavigationCandidatePageSearchValues(result.item),
        search: query,
        settingLabels: result.item.matchedSettingLabels,
        settingValues: getSettingsNavigationCandidateSettingSearchValues(
          result.item,
        ),
      });
      if (!searchEvidence) {
        continue;
      }
      if (
        shippingNewSettings &&
        searchEvidence.kind === "setting" &&
        !result.item.matchedSettingRecords.some(
          (record) =>
            getSettingsSearchRank({
              search: query,
              label: record.label,
              terms: [result.item.groupTitle, ...record.terms],
            }) !== undefined,
        ) &&
        getSettingsSearchRank({
          search: query,
          label: result.item.groupTitle,
          terms: [
            result.item.groupDescription,
            ...result.item.groupSearchTerms,
          ],
        }) === undefined
      ) {
        continue;
      }

      const existing = candidatesByHref.get(result.item.item.href);
      const fuseScore = result.score ?? 0;
      if (!existing || fuseScore < existing.fuseScore) {
        const candidate = addSearchEvidenceToSettingsNavigationCandidate({
          shippingNewSettings,
          candidate: result.item,
          searchEvidence,
        });
        candidatesByHref.set(result.item.item.href, { candidate, fuseScore });
      }
    }
  }

  return [...candidatesByHref.values()]
    .sort((a, b) => {
      return (
        a.fuseScore - b.fuseScore ||
        b.candidate.catalogScore - a.candidate.catalogScore
      );
    })
    .map((result) => result.candidate);
}

function addSearchEvidenceToSettingsNavigationCandidate({
  shippingNewSettings,
  candidate,
  searchEvidence,
}: {
  shippingNewSettings: boolean;
  candidate: SettingsNavigationSearchCandidate;
  searchEvidence: SettingSearchEvidence;
}): SettingsNavigationSearchCandidate {
  return {
    ...candidate,
    item: {
      ...candidate.item,
      ...(shippingNewSettings
        ? {
            settingsResult: candidate.item.settingsResult && {
              ...candidate.item.settingsResult,
              ...(searchEvidence.kind === "page"
                ? { kind: "page", label: candidate.pageFriendlyName }
                : {}),
            },
          }
        : {}),
      subtitle: getSettingsNavigationSearchEvidenceSubtitle({
        shippingNewSettings,
        currentSubtitle: candidate.item.subtitle,
        searchEvidence,
      }),
    },
    searchEvidence,
  };
}

function getSettingsNavigationSearchEvidenceSubtitle({
  shippingNewSettings,
  currentSubtitle,
  searchEvidence,
}: {
  shippingNewSettings: boolean;
  currentSubtitle?: string;
  searchEvidence: SettingSearchEvidence;
}): string | undefined {
  switch (searchEvidence.kind) {
    case "page":
      return shippingNewSettings
        ? t("Matched page: {{label}}", { label: searchEvidence.label })
        : `Matched page: ${searchEvidence.label}`;
    case "group":
      return shippingNewSettings
        ? t("Matched group: {{label}}", { label: searchEvidence.label })
        : `Matched group: ${searchEvidence.label}`;
    case "setting":
      return currentSubtitle;
  }

  return currentSubtitle;
}

function getSettingsNavigationCandidatePageSearchValues(
  candidate: SettingsNavigationSearchCandidate,
): string[] {
  return [
    candidate.pageFriendlyName,
    candidate.pageTitle,
    ...candidate.routeSearchTerms,
  ];
}

function getSettingsNavigationCandidateGroupSearchValues(
  candidate: SettingsNavigationSearchCandidate,
): string[] {
  return [
    candidate.groupTitle,
    candidate.groupDescription,
    ...candidate.groupSearchTerms,
  ];
}

function getSettingsNavigationCandidateSettingSearchValues(
  candidate: SettingsNavigationSearchCandidate,
): string[] {
  return [
    ...candidate.matchedSettingDescriptions,
    ...candidate.matchedSettingLabels,
    ...candidate.matchedSettingSearchTerms,
  ];
}

function getSettingsNavigationSearchQueryVariants(query: string): string[] {
  return getUniqueStrings([
    query,
    ...query.split(/[-–—:;,.!?]/).map((part) => part.trim()),
    getSettingsNavigationKeywordQuery(query),
  ]);
}

function getSettingsNavigationKeywordQuery(query: string): string {
  return query
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(
      (token) =>
        token.length > 0 && !SETTINGS_NAVIGATION_SEARCH_STOP_WORDS.has(token),
    )
    .join(" ");
}

function getGroupVisibleSearchTerms(
  group: SettingGroupDefinition | undefined,
): string[] {
  if (!group) {
    return [];
  }

  return getUniqueStrings([
    group.title,
    group.description,
    ...(group.synonyms ?? []),
  ]);
}

function getCatalogDefinitionVisibleSearchTerms(
  definition: SettingDefinition,
  shippingNewSettings: boolean,
): string[] {
  const metadata = definition.metadata;

  return getUniqueStrings([
    metadata.label,
    metadata.description,
    ...(shippingNewSettings ? (metadata.searchLabels ?? []) : []),
    ...(metadata.synonyms ?? []),
    ...getCatalogDefinitionValueSemanticsSearchTerms(definition),
  ]);
}

function getSettingsNavigationRouteSearchTerms(
  hrefs: readonly string[],
): string[] {
  return getUniqueStrings(
    hrefs.flatMap((href) => {
      const terms = getRouteSearchTerms(
        canonicalizeSettingsNavigationHref(href),
      );
      return [terms.join(" "), ...terms];
    }),
  );
}

function getAccessibleSettingsNavigationItems(
  sections: readonly SettingsSection[],
  shippingNewSettings: boolean,
): AccessibleSettingsNavigationItem[] {
  return sections.flatMap((section) =>
    getAccessibleSettingsNavigationItemsFromItems({
      items: section.items,
      shippingNewSettings,
      sectionName: (shippingNewSettings
        ? newSettingsSectionToFriendly
        : settingsSectionToFriendly)[section.name],
    }),
  );
}

function getAccessibleSettingsNavigationItemsFromItems({
  items,
  sectionName,
  shippingNewSettings,
}: {
  items: readonly SettingItem[];
  shippingNewSettings: boolean;
  sectionName: string;
}): AccessibleSettingsNavigationItem[] {
  return items.flatMap((item) => [
    {
      canonicalHref: canonicalizeSettingsNavigationHref(item.href),
      item,
      sectionName,
    },
    ...(shippingNewSettings
      ? getAccessibleSettingsNavigationItemsFromItems({
          items: item.children ?? [],
          shippingNewSettings,
          sectionName,
        })
      : []),
  ]);
}

function findAccessibleSettingsNavigationItem({
  accessibleItems,
  href,
  routes,
}: {
  accessibleItems: readonly AccessibleSettingsNavigationItem[];
  href: string;
  routes: SettingsNavigationSearchRoutes;
}): AccessibleSettingsNavigationItem | undefined {
  const candidates = getCanonicalRouteCandidates(href, routes);

  for (const candidate of candidates) {
    const exactMatch = accessibleItems.find(
      (item) => item.canonicalHref === candidate,
    );
    if (exactMatch) {
      return exactMatch;
    }
  }

  return accessibleItems
    .filter((item) =>
      candidates.some((candidate) =>
        isCanonicalRouteOwnedBy(candidate, item.canonicalHref),
      ),
    )
    .sort((a, b) => b.canonicalHref.length - a.canonicalHref.length)[0];
}

function getCanonicalRouteCandidates(
  href: string,
  routes: SettingsNavigationSearchRoutes,
): string[] {
  const canonicalHref = canonicalizeSettingsNavigationHref(href);
  // FAQ's settings-catalog full-page target is still the legacy /support/faq
  // path (redirects to /support-v2/faq), so match against that legacy base.
  const legacyFaqHref = canonicalizeSettingsNavigationHref(
    `${routes.legacySupportBaseUrl}/faq`,
  );
  const settingsFaqHref = canonicalizeSettingsNavigationHref(
    routes.getSettingsSubRoute("faq"),
  );

  const candidates = [canonicalHref];
  if (isCanonicalRouteOwnedBy(canonicalHref, legacyFaqHref)) {
    candidates.push(settingsFaqHref);
  }
  if (canonicalHref === settingsFaqHref) {
    candidates.push(legacyFaqHref);
  }

  return getUniqueStrings(candidates);
}

function isCanonicalRouteOwnedBy(
  canonicalHref: string,
  ownerCanonicalHref: string,
): boolean {
  return (
    canonicalHref === ownerCanonicalHref ||
    canonicalHref.startsWith(`${ownerCanonicalHref}/`)
  );
}

function getVisibleDefinitionsForGroup({
  groupId,
  shippingNewSettings,
  viewerVisibility,
}: {
  groupId: string;
  shippingNewSettings: boolean;
  viewerVisibility: SettingVisibility;
}): readonly SettingDefinition[] {
  return getSettingCatalogDefinitionsForGroup(
    groupId,
    shippingNewSettings,
  ).filter((definition) =>
    canReadSetting(viewerVisibility, definition.metadata.access),
  );
}

function getCatalogSearchTerms({
  definitions,
  href,
  shippingNewSettings,
  group,
}: {
  definitions: readonly SettingDefinition[];
  href: string;
  shippingNewSettings: boolean;
  group: SettingGroupDefinition;
}): string[] {
  if (!shippingNewSettings) {
    return getUniqueStrings([
      group.id,
      group.title,
      group.description,
      ...(group.synonyms ?? []),
      href,
      ...getRouteSearchTerms(href),
      ...definitions.flatMap((definition) => [
        definition.id,
        definition.metadata.label,
        definition.metadata.description,
        ...(definition.metadata.synonyms ?? []),
        ...getCatalogDefinitionValueSemanticsSearchTerms(definition),
      ]),
    ]);
  }
  return getUniqueStrings([
    ...getGroupVisibleSearchTerms(group),
    ...definitions.flatMap((definition) =>
      getCatalogDefinitionVisibleSearchTerms(definition, true),
    ),
  ]);
}

function getCatalogSearchHints({
  definitions,
  group,
}: {
  definitions: readonly SettingDefinition[];
  group: SettingGroupDefinition;
}): string[] {
  return getUniqueStrings([
    group.title,
    ...definitions.map((definition) => definition.metadata.label),
  ]);
}

function getCatalogSearchMatches({
  definitions,
  shippingNewSettings,
  group,
  href,
}: {
  definitions: readonly SettingDefinition[];
  shippingNewSettings: boolean;
  group: SettingGroupDefinition;
  href: string;
}): SettingItemSearchMatch[] {
  if (!shippingNewSettings) {
    return definitions.map((definition) => ({
      href: getSettingsFocusedSettingHref({
        groupId: group.id,
        href,
        settingId: definition.id,
      }),
      label: definition.metadata.label,
      searchTerms: getCatalogDefinitionSearchTerms({ definition }),
      settingId: definition.id,
    }));
  }
  const matches = definitions.flatMap((definition) => {
    const hrefForSetting = getSettingsFocusedSettingHref({
      groupId: group.id,
      href,
      settingId: definition.id,
    });
    const matches: SettingItemSearchMatch[] =
      definition.metadata.label === group.title
        ? []
        : [
            {
              href: hrefForSetting,
              label: definition.metadata.label,
              contextLabel: group.title,
              searchTerms: getCatalogDefinitionVisibleSearchTerms(
                definition,
                true,
              ),
              settingId: definition.id,
            },
          ];
    for (const label of definition.metadata.searchLabels ?? []) {
      matches.push({
        href:
          (group.id === "fulfillment.auto_merge" ||
            group.id === "fulfillment.merge_suggestions") &&
          definition.metadata.label === group.title
            ? getSettingsFocusedGroupHref({ groupId: group.id, href })
            : hrefForSetting,
        label,
        contextLabel: group.title,
        settingId: definition.id,
        searchTerms: [label],
      });
    }
    return matches;
  });
  return [
    ...matches,
    ...(group.searchLabels ?? []).map((label) => ({
      label,
      href: getSettingsFocusedGroupHref({ groupId: group.id, href }),
      searchTerms: [label],
    })),
  ];
}

function getCatalogDefinitionSearchTerms({
  definition,
}: {
  definition: SettingDefinition;
}): string[] {
  const metadata = definition.metadata;

  return getUniqueStrings([
    definition.id,
    ...getRouteSearchTerms(definition.id),
    metadata.label,
    metadata.description,
    ...(metadata.synonyms ?? []),
    ...getCatalogDefinitionValueSemanticsSearchTerms(definition),
  ]);
}

function getCatalogDefinitionValueSemanticsSearchTerms(
  definition: SettingDefinition,
): string[] {
  const valueSemantics = definition.metadata.valueSemantics;
  if (!valueSemantics) {
    return [];
  }

  return getUniqueStrings([
    valueSemantics.inputKind,
    valueSemantics.unit,
    valueSemantics.trueLabel,
    valueSemantics.falseLabel,
    valueSemantics.unsetLabel,
    ...(valueSemantics.options ?? [])
      .filter(isSearchableValueSemanticsOption)
      .flatMap((option) => [option.value, option.label, option.description]),
  ]);
}

function isSearchableValueSemanticsOption({
  label,
  value,
}: {
  label: string;
  value: string;
}): boolean {
  const optionTokens = [value, label].flatMap((term) =>
    getRouteSearchTerms(term).map((token) => token.toLowerCase()),
  );

  return !optionTokens.some((token) =>
    VALUE_SEMANTICS_OPTION_SEARCH_STOP_WORDS.has(token),
  );
}

function getRouteSearchTerms(href: string): string[] {
  return href
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(/[^a-zA-Z0-9]+/)
    .map((term) => term.trim())
    .filter((term) => term.length > 0);
}

function getOrCreateSearchTerms(
  itemSearchTerms: Map<SettingItem, Set<string>>,
  item: SettingItem,
): Set<string> {
  return getOrCreateSearchValues(itemSearchTerms, item);
}

function getOrCreateSearchMatches(
  itemSearchMatches: Map<SettingItem, SettingItemSearchMatch[]>,
  item: SettingItem,
): SettingItemSearchMatch[] {
  const existing = itemSearchMatches.get(item);
  if (existing) {
    return existing;
  }

  const created: SettingItemSearchMatch[] = [];
  itemSearchMatches.set(item, created);
  return created;
}

function addSearchMatch(
  matches: SettingItemSearchMatch[],
  match: SettingItemSearchMatch,
): void {
  if (
    matches.some(
      (existing) =>
        existing.label.toLowerCase() === match.label.toLowerCase() &&
        existing.href === match.href,
    )
  ) {
    return;
  }

  matches.push(match);
}

function getOrCreateSearchValues(
  itemSearchValues: Map<SettingItem, Set<string>>,
  item: SettingItem,
): Set<string> {
  const existing = itemSearchValues.get(item);
  if (existing) {
    return existing;
  }

  const created = new Set<string>();
  itemSearchValues.set(item, created);
  return created;
}

function addSearchValue(values: Set<string>, value: string | undefined): void {
  const trimmedValue = value?.trim();
  if (!trimmedValue) {
    return;
  }

  values.add(trimmedValue);
}

function getUniqueStrings(values: readonly (string | undefined)[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const value of values) {
    const trimmedValue = value?.trim();
    if (!trimmedValue) {
      continue;
    }

    const key = trimmedValue.toLowerCase();
    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    result.push(trimmedValue);
  }

  return result;
}

function normalizePathname(pathname: string): string {
  if (pathname.length <= 1) {
    return pathname;
  }

  return pathname.replace(/\/+$/, "");
}
